/**
 * How long a profile opened from the matching page was actually read
 * (2026-10-27). Mounted once in ProfileLayout, which stays up while the
 * profile's tabs (Profile, Platform Profile, Interviews, ...) swap, so one
 * open is followed across them.
 *
 * Only a profile opened from the matching page is studied: its link carries
 * the open id the match page minted at the click (?matchOpen=), the search
 * response it came from (?matchView=) and the opening (?shortlistOpp=)
 * (src/utils/shortlistAttribution.ts). Switching tabs drops the query, so the
 * open's running totals are kept in sessionStorage per profile, and every
 * history entry the open is seen on is stamped with its id (history.state,
 * beside the router's own). A reload or Back to such an entry carries on. Any
 * other visit records nothing: a fresh visit, a copied URL, or a later one to
 * the same profile in this tab by another route (search, another page).
 *
 * What is reported (POST /api/platform/matching/:opening/profile-opens/:openId/study,
 * server profileOpens.service.ts), all running totals:
 *   - attended time on the profile, as src/utils/attentionClock.ts defines it
 *     (the profile loaded and showing, tab visible, no dialog open, the
 *     specialist active within 30 s; stretches under 1 s dropped, each capped
 *     at 120 s);
 *   - tabs: the profile tabs visited;
 *   - sections: the Platform Profile sections attended for at least 1 s
 *     (half of the section, or half the screen, on screen);
 *   - the deepest point the screen reached through those sections, as a %.
 * Sent every PROFILE_FLUSH_MS when something changed, and with keepalive when
 * the tab hides, the page closes or another profile opens. Quiet (an auth
 * error never navigates), one more try for a report lost to the network or a
 * 5xx, none for a 4xx. A failed report never reaches the page. Ids only: the
 * candidate's id is the route's, sent to our own API, never written to the DOM.
 */
import { createContext, useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { apiFetch } from '../../../store/services/api';
import { useAppSelector } from '../../../store/types';
import {
  AttentionClock,
  AttentionTotals,
  emptyTotals,
  isOnScreen,
  isTotals,
  listenForActivity,
  toSeconds,
} from '../../../utils/attentionClock';

export const PROFILE_FLUSH_MS = 15_000;
export const STUDY_STORAGE_KEY = 'profileStudy.v1';
/** The server ignores reports this long after the open; so does the page. */
const OPEN_WINDOW_MS = 12 * 60 * 60 * 1000;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const THRESHOLDS = Array.from({ length: 21 }, (_, i) => i / 20);
/** An open MUI dialog or modal drawer over the page (portalled to <body>); a closed keepMounted one is hidden. Menus and selects don't count. */
const DIALOG_SELECTOR = 'body > .MuiDialog-root:not(.MuiModal-hidden), body > .MuiDrawer-modal:not(.MuiModal-hidden)';
/** The history.state field that says which open a history entry belongs to. */
const ENTRY_FIELD = 'matchOpen';
const PAGE = 'page';

interface Open {
  openId: string;
  viewId: string | null;
  jobOpeningId: string;
  environment: string;
  openedAt: number;
}
interface Saved extends Open {
  totals: AttentionTotals;
  sections: string[];
  tabs: string[];
  maxScroll: number | null;
}

/** What PlatformProfilePage registers its sections with (null outside a studied profile's layout). */
export interface ProfileStudy {
  section: (name: string, el: HTMLElement | null) => void;
}
export const ProfileStudyContext = createContext<ProfileStudy | null>(null);

function readSaved(): Record<string, Saved> {
  try {
    const raw = JSON.parse(window.sessionStorage.getItem(STUDY_STORAGE_KEY) || '{}');
    return raw && typeof raw === 'object' ? raw : {};
  } catch {
    return {};
  }
}

/** The tab a profile path is on: the segment after the profile id, or "profile". */
export function profileTab(pathname: string, profileId: string): string {
  const rest = pathname.split(`/profile/${profileId}`)[1] ?? '';
  return rest.split('/').filter(Boolean)[0] || 'profile';
}

const retryable = (err: any) => !err?.status || err.status >= 500;

/** The open this history entry was stamped with, if any (it survives a reload and Back; a new entry has none). */
function entryOpen(): string | null {
  const id = window.history.state?.[ENTRY_FIELD];
  return typeof id === 'string' ? id : null;
}

/**
 * Stamps the history entry on screen with the open, keeping the router's own state, and takes the
 * link's open id and record out of the address bar, so a copied or bookmarked URL is never this open.
 */
function stampEntry(openId: string): void {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('matchOpen');
    url.searchParams.delete('matchView');
    window.history.replaceState({ ...(window.history.state ?? {}), [ENTRY_FIELD]: openId }, '', `${url.pathname}${url.search}${url.hash}`);
  } catch {
    /* not stamped: a reload of this entry then starts no open */
  }
}

export function useProfileStudy(profileId: string | undefined): ProfileStudy {
  const [searchParams] = useSearchParams();
  const { pathname, key: locationKey } = useLocation();
  const environment = useAppSelector((s) => s.environment.current) as string;
  // The profile is on the page: its loading state is nothing to read.
  const loaded = useAppSelector((s) => {
    const p = (s as any).profile;
    return !!profileId && p?.profileId === profileId && !!p?.data && !p?.loading;
  });
  const loadedRef = useRef(loaded);
  loadedRef.current = loaded;

  // ─── Which open this is (from the link, else from this tab's storage) ───
  const open = useMemo<Open | null>(() => {
    if (!profileId || !UUID.test(profileId)) return null;
    const openId = searchParams.get('matchOpen') || '';
    const jobOpeningId = searchParams.get('shortlistOpp') || '';
    const saved = readSaved()[profileId];
    if (UUID.test(openId) && UUID.test(jobOpeningId)) {
      if (saved?.openId === openId) return saved;
      const viewId = searchParams.get('matchView') || '';
      return { openId, jobOpeningId, viewId: UUID.test(viewId) ? viewId : null, environment, openedAt: Date.now() };
    }
    if (saved && UUID.test(saved.openId) && Date.now() - saved.openedAt < OPEN_WINDOW_MS && entryOpen() === saved.openId) return saved;
    return null;
    // Decided once per profile: later tab switches drop the query but keep the open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId]);

  const stateRef = useRef<{ clock: AttentionClock; tabs: Set<string> } | null>(null);
  const elements = useRef(new Map<string, HTMLElement>());
  const observer = useRef<IntersectionObserver | null>(null);
  /** Measures the scroll depth on the next frame (set while an open is studied). */
  const measureSoon = useRef<(() => void) | null>(null);

  // ─── The open's clock: started for this profile, stopped and sent when it goes ───
  // Everything that sends lives in here, so a report always goes to its own open,
  // also when the next profile has already taken over the layout.
  useEffect(() => {
    if (!open || !profileId) return undefined;
    const saved = readSaved()[profileId];
    const restored = saved?.openId === open.openId ? saved : null;
    const clock = new AttentionClock(undefined, { visible: document.visibilityState !== 'hidden' });
    clock.restore(PAGE, isTotals(restored?.totals) ? restored!.totals : emptyTotals());
    clock.setPaused(!!document.querySelector(DIALOG_SELECTOR));
    clock.setOnScreen(PAGE, loadedRef.current);
    const sections = new Set<string>(Array.isArray(restored?.sections) ? restored!.sections : []);
    const tabs = new Set<string>(Array.isArray(restored?.tabs) ? restored!.tabs : []);
    const restoredScroll: number | null = typeof restored?.maxScroll === 'number' ? restored.maxScroll : null;
    /** How far (px) the bottom of the screen has reached into the sections; the % is taken at send time. */
    let maxReach: number | null = null;
    let lastScroll: number | null = restoredScroll;
    let sent = '';
    let retried = false;
    let alive = true;
    const st = { clock, tabs };
    stateRef.current = st;
    const canCount = () => document.visibilityState !== 'hidden' && !document.querySelector(DIALOG_SELECTOR);

    /** The sections' extent on screen now, or null with none registered. */
    const bounds = () => {
      if (!elements.current.size) return null;
      let top = Infinity;
      let bottom = -Infinity;
      elements.current.forEach((el) => {
        const r = el.getBoundingClientRect();
        top = Math.min(top, r.top);
        bottom = Math.max(bottom, r.bottom);
      });
      return bottom > top ? { top, bottom } : null;
    };
    /**
     * The deepest point reached, as a % of the sections as they are now: content that loads after the
     * screen got somewhere (interviews, say) makes the page longer, so the % is not fixed early.
     */
    const pctOf = (b: { top: number; bottom: number }, reach: number) => {
      const pct = Math.round(Math.min(100, Math.max(0, (reach / (b.bottom - b.top)) * 100)));
      return restoredScroll === null ? pct : Math.max(restoredScroll, pct);
    };
    /** Sections gone (another tab, the profile left) keep the last % they gave. */
    const scrollPct = (): number | null => {
      const b = maxReach === null ? null : bounds();
      if (b && maxReach !== null) lastScroll = pctOf(b, maxReach);
      return lastScroll;
    };

    const save = () => {
      const maxScroll = scrollPct();
      try {
        const all = readSaved();
        for (const [key, v] of Object.entries(all)) if (!(Date.now() - (v as Saved)?.openedAt < OPEN_WINDOW_MS)) delete all[key];
        all[profileId] = { ...open, totals: clock.totals(PAGE), sections: [...sections], tabs: [...tabs], maxScroll };
        window.sessionStorage.setItem(STUDY_STORAGE_KEY, JSON.stringify(all));
      } catch {
        /* storage blocked: a reload then starts again from nothing */
      }
    };

    const flush = (keepalive = false) => {
      clock.keys().forEach((key) => {
        if (key.startsWith('section:') && clock.totals(key).stretches > 0) sections.add(key.slice(8));
      });
      save();
      const body = {
        candidateId: profileId,
        viewId: open.viewId,
        ...toSeconds(clock.totals(PAGE)),
        maxScrollPct: scrollPct(),
        sections: [...sections].sort(),
        tabs: [...tabs].sort(),
      };
      const sig = JSON.stringify(body);
      if (sig === sent) return;
      sent = sig;
      try {
        Promise.resolve(apiFetch(`/api/platform/matching/${open.jobOpeningId}/profile-opens/${open.openId}/study`, {
          environment: open.environment as any,
          method: 'POST',
          body: { ...body, openedAgoMs: Math.max(0, Date.now() - open.openedAt) },
          keepalive,
          quiet: true,
        })).then(() => { retried = false; }, (err) => {
          if (retried || !retryable(err)) return;
          retried = true;
          sent = ''; // the next flush sends it again; at once if the page has gone
          if (!alive) flush(true);
        });
      } catch {
        /* dropped */
      }
    };

    const nameOf = (el: Element) => {
      for (const [name, known] of elements.current) if (known === el) return name;
      return null;
    };
    const io = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const name = nameOf(entry.target);
        if (name) clock.setOnScreen(`section:${name}`, isOnScreen(entry));
      }
    }, { threshold: THRESHOLDS });
    observer.current = io;
    elements.current.forEach((el) => io?.observe(el));

    let frame: number | null = null;
    /** How far the bottom of the screen has reached through the Platform Profile's sections. */
    const measure = () => {
      frame = null;
      const b = canCount() ? bounds() : null;
      if (!b) return;
      maxReach = Math.max(maxReach ?? 0, window.innerHeight - b.top);
      lastScroll = pctOf(b, maxReach);
    };
    const onScroll = () => {
      if (frame === null) frame = window.requestAnimationFrame(measure);
    };
    measureSoon.current = onScroll;
    const onVisibility = () => {
      clock.setVisible(document.visibilityState !== 'hidden');
      if (document.visibilityState === 'hidden') flush(true);
      else onScroll();
    };
    const onPageHide = () => flush(true);
    const checkDialogs = () => {
      const covered = !!document.querySelector(DIALOG_SELECTOR);
      clock.setPaused(covered);
      if (!covered) onScroll();
    };
    const dialogs = new MutationObserver(checkDialogs);
    dialogs.observe(document.body, { childList: true });
    // A keepMounted modal opens by a class change the observer can't see: checked on activity too.
    const stopActivity = listenForActivity(() => {
      clock.setPaused(!!document.querySelector(DIALOG_SELECTOR));
      clock.activity();
    });
    window.addEventListener('scroll', onScroll, { capture: true, passive: true });
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    const interval = setInterval(() => flush(), PROFILE_FLUSH_MS);
    frame = window.requestAnimationFrame(measure);
    return () => {
      alive = false;
      if (measureSoon.current === onScroll) measureSoon.current = null;
      clearInterval(interval);
      stopActivity();
      dialogs.disconnect();
      io?.disconnect();
      if (observer.current === io) observer.current = null;
      if (frame !== null) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll, { capture: true } as EventListenerOptions);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      clock.stopAll();
      flush(true);
      if (stateRef.current === st) stateRef.current = null;
    };
  }, [open, profileId]);

  useEffect(() => {
    stateRef.current?.clock.setOnScreen(PAGE, loaded);
  }, [open, loaded]);

  // Every entry the open is seen on (the landing one, each profile tab) carries its id.
  useEffect(() => {
    if (open) stampEntry(open.openId);
  }, [open, locationKey]);

  // ─── The tabs visited ───
  useEffect(() => {
    if (profileId) stateRef.current?.tabs.add(profileTab(pathname, profileId));
  }, [open, profileId, pathname]);

  // Stable for the layout's life: sections register once, whatever the open.
  const section = useCallback((name: string, el: HTMLElement | null) => {
    const prev = elements.current.get(name);
    if (prev === el) return;
    if (prev) {
      observer.current?.unobserve(prev);
      elements.current.delete(name);
      stateRef.current?.clock.setOnScreen(`section:${name}`, false);
    }
    if (el) {
      elements.current.set(name, el);
      observer.current?.observe(el);
      measureSoon.current?.(); // a section that arrives later (the profile loaded) counts from where the screen is
    }
  }, []);
  return useMemo(() => ({ section }), [section]);
}
